import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { LineThroughputBatchResponse } from "@situate/contracts/runtime";
import {
  formatAnalyticsPeriodLabel,
  getTotalPopulationTrendQueryRange,
} from "@/features/deployment-dashboard/shared/analytics-query-range";
import {
  computeThroughputTotals,
  mapThroughputDataPointsToTrend,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-trend-chart.utils";
import {
  getThroughputLineOptionKey,
  type ThroughputLineTrendData,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const LINE_THROUGHPUT_BATCH_PATH = "/v1/analytics/line-throughput/batch";

interface UseThroughputTrendParams {
  deploymentId: string;
  /** Throughput lines to fetch trend data for. */
  selectedLines: ThroughputLineOption[];
  startDate: Date;
  endDate: Date;
}

interface ThroughputTrendState {
  lines: ThroughputLineTrendData[];
  loading: boolean;
  error: Error | null;
}

/**
 * Fetches throughput trend data for every given line in a single batch
 * request to the runtime API's `line-throughput/batch` endpoint, instead of
 * one request per line. The API authorizes access per line and returns data
 * only for the subset the caller can access; lines outside the caller's
 * grant are simply absent from the response rather than causing an error.
 */
export function useThroughputTrend({
  deploymentId,
  selectedLines,
  startDate,
  endDate,
}: UseThroughputTrendParams): ThroughputTrendState {
  const runtime = useRuntimeHttpClient();
  const hasLineThroughputGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "analytics_line_throughput"
  );
  const client = runtime.client;
  const { granularity, startTime, endTime } = getTotalPopulationTrendQueryRange(startDate, endDate);
  const isEnabled =
    Boolean(client) &&
    runtime.deploymentId === deploymentId &&
    hasLineThroughputGrant &&
    selectedLines.length > 0;

  // Content-derived signature the query keys off of, so a re-render that produces a new
  // `selectedLines` array reference with the same lines does not trigger a refetch.
  const lineKeysSignature = [...selectedLines].map(getThroughputLineOptionKey).sort().join(",");

  const query = useQuery({
    queryKey: [
      "throughput-trend-batch",
      deploymentId,
      lineKeysSignature,
      startTime,
      endTime,
      granularity,
    ],
    queryFn: async ({ signal }): Promise<LineThroughputBatchResponse> => {
      if (!client) throw new Error("Runtime client is required");
      return client.post<LineThroughputBatchResponse>(
        LINE_THROUGHPUT_BATCH_PATH,
        {
          lines: selectedLines.map((line) => ({
            area_id: line.areaId,
            line_id: line.line_id,
          })),
          start_time: startTime,
          end_time: endTime,
          granularity,
        },
        { signal }
      );
    },
    enabled: isEnabled,
  });

  // Memoized so `lines` keeps a stable array reference across renders that don't touch the
  // query result, selected lines, or granularity. Without this, every render (including ones
  // triggered by unrelated state elsewhere on the page) would rebuild a fresh array here, which
  // in turn would defeat memoization in every downstream consumer (e.g. the chart's series/data
  // computation), forcing an expensive chart rebuild on every render.
  const resultsByKey = useMemo(
    () =>
      new Map(
        (query.data?.results ?? []).map((result) => [
          getThroughputLineOptionKey({ areaId: result.area_id, line_id: result.line_id }),
          result,
        ])
      ),
    [query.data]
  );

  const lines: ThroughputLineTrendData[] = useMemo(
    () =>
      selectedLines.map((line) => {
        const result = resultsByKey.get(getThroughputLineOptionKey(line));

        const mappedPoints = result
          ? mapThroughputDataPointsToTrend(result.data_points, line, (point) =>
              formatAnalyticsPeriodLabel(point.window_start, granularity)
            )
          : [];
        const points = mappedPoints.map((point) => ({
          ...point,
          period: formatAnalyticsPeriodLabel(point.timestamp, granularity),
        }));
        const { totalIn, totalOut, net } = computeThroughputTotals(points);
        return {
          line,
          points,
          totalIn,
          totalOut,
          net,
          loading: query.isLoading,
          error: query.error,
        };
      }),
    [selectedLines, resultsByKey, granularity, query.isLoading, query.error]
  );

  return {
    lines,
    loading: query.isLoading,
    error: query.error,
  };
}
